import React from "react";
import PropTypes from "prop-types";
import Box from "../wrappers/Box";
import Dialog from "../wrappers/Dialog";
import DialogActions from "../wrappers/DialogActions";
import DialogContent from "../wrappers/DialogContent";
import DialogTitle from "../wrappers/DialogTitle";
import {
  SearchFiltersSectionsProvider,
  SearchFiltersSectionsToggle,
} from "./SearchFiltersSectionsContext";

/**
 * Renderiza o shell full-screen de filtros para composição mobile.
 *
 * @param {object} pProps Propriedades do componente.
 * @param {string} pProps.id Identificador estável do diálogo.
 * @param {string} pProps.title Título associado ao diálogo.
 * @param {React.ReactNode} [pProps.description] Descrição complementar.
 * @param {boolean} pProps.open Controla a visibilidade do diálogo.
 * @param {React.ReactNode} pProps.children Corpo rolável dos filtros.
 * @param {React.ReactNode} pProps.actions Ações fixas do rodapé.
 * @param {Function} pProps.onClose Recebe o motivo normalizado do fechamento.
 * @returns {React.ReactElement}
 */
const SearchFiltersMobileDialogContent = (pProps) => {
  const { id, title, description, open, children, actions, onClose } = pProps;
  const xTitleId = `${id}-title`;

  /** Normaliza os motivos do wrapper MUI para o contrato público. */
  const pvHandleClose = (pEvent, pReason) => {
    onClose(
      pReason === "backdropClick"
        ? "backdrop"
        : pReason === "escapeKeyDown"
        ? "escape"
        : pReason
    );
  };

  return (
    <Dialog
      aria-labelledby={xTitleId}
      fullScreen
      onClose={pvHandleClose}
      open={open}
      PaperProps={{
        sx: { display: "flex", flexDirection: "column", minHeight: 0 },
      }}
    >
      <DialogTitle
        data-search-filters-mobile-dialog-title
        id={`${id}-header`}
        onClose={() => onClose("closeButton")}
        sx={{
          alignItems: "center",
          display: "flex",
          flexShrink: 0,
          gap: 1,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flex: 1,
            gap: 2,
            justifyContent: "flex-start",
            minWidth: 0,
          }}
        >
          <span id={xTitleId}>{title}</span>
          <SearchFiltersSectionsToggle align="start" />
        </Box>
      </DialogTitle>
      <DialogContent
        data-search-filters-mobile-dialog-body
        dividers
        sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
      >
        {description ? (
          <Box color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Box>
        ) : null}
        {children}
      </DialogContent>
      <DialogActions
        data-search-filters-mobile-dialog-actions
        sx={{ flexShrink: 0, px: 3, py: 2 }}
      >
        {actions}
      </DialogActions>
    </Dialog>
  );
};

/** Isola o estado de abertura das seções pertencentes a este diálogo. */
const SearchFiltersMobileDialog = (pProps) => (
  <SearchFiltersSectionsProvider>
    <SearchFiltersMobileDialogContent {...pProps} />
  </SearchFiltersSectionsProvider>
);

SearchFiltersMobileDialog.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  open: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  actions: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SearchFiltersMobileDialog;
